import { Student } from "../providers/studentsProvider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddIcon from "@mui/icons-material/Add";
import { Row } from "../misc/Row";
import { autovioColors } from "../misc/backofficeTheme";
import {
  Box,
  Button,
  IconButton,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  Typography,
} from "@mui/material";
import { BookedQuote } from "../providers/bookedQuotesProvider";
import { useEffect, useRef, useState } from "react";
import {
  DrivingLicenseClass,
  isForBus,
  isForCarOnly,
  isForCarWithTrailer,
  isForMotorcycle,
  isForTractor,
  isForTruck,
} from "../model/DrivingLicenseClass";
import { CarIcon } from "../icons/CarIcon";
import { MotorcycleIcon } from "../icons/MotorcycleIcon";
import { TrailerIcon } from "../icons/TrailerIcon";
import { TruckIcon } from "../icons/TruckIcon";
import { BusIcon } from "../icons/BusIcon";
import { TractorIcon } from "../icons/TractorIcon";
import { Column } from "../misc/Column";
import { useBookedQuotes } from "../hooks/useBookedQuotes";
import { useDialog } from "../hooks/useDialog";
import { BookedQuoteDialog, bookedQuoteShownInBookedQuoteDialogState } from "../dialogs/BookedQuoteDialog";
import { useSetRecoilState } from "recoil";
import { grants } from "../backoffice.access_control";
import { BookedQuotesDialog } from "../dialogs/BookedQuotesDialog";
import { AddBundleDialog } from "../dialogs/AddBundleDialog";

/** See https://autovio.atlassian.net/browse/APP-5633 */

export function StudentBookedQuotes({ student }: { student: Student }) {
  const { bookedQuotes, selectedBookedQuote, setSelectedBookedQuote } = useBookedQuotes(student);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  return (
    <Row
      justifyContent="space-between"
      sx={{
        width: "100%",
        mb: 2,
      }}
    >
      <_SelectedBookedQuote
        open={!!anchorEl}
        bookedQuotes={bookedQuotes}
        selectedBookedQuote={selectedBookedQuote}
        openMenu={openMenu}
      />
      {bookedQuotes.length > 0 ? (
        <_BookedQuotesMenu
          anchorEl={anchorEl}
          closeMenu={closeMenu}
          bookedQuotes={bookedQuotes}
          selectedBookedQuote={selectedBookedQuote}
          setSelectedBookedQuote={setSelectedBookedQuote}
        />
      ) : null}
      <_ManageBookedQuotes student={student} bookedQuotes={bookedQuotes} />
    </Row>
  );
}

interface _SelectedBookedQuoteProps {
  open: boolean;
  bookedQuotes: BookedQuote[];
  selectedBookedQuote: BookedQuote;
  openMenu: (event: React.MouseEvent<HTMLElement>) => void;
}
function _SelectedBookedQuote({ open, bookedQuotes, selectedBookedQuote, openMenu }: _SelectedBookedQuoteProps) {
  const hasManyQuotes = bookedQuotes.length > 1;
  const secondaryText = (() => {
    const parts: string[] = [];
    if (selectedBookedQuote.createdAt) {
      parts.push(`Gestartet am ${selectedBookedQuote.createdAt.toFormat("dd.MM.yyyy")}`);
    }
    if (selectedBookedQuote.cancelledAt) {
      parts.push(`Abgebrochen am ${selectedBookedQuote.cancelledAt.toFormat("dd.MM.yyyy")}`);
    }
    return parts.length ? parts.join(" • ") : null;
  })();
  return (
    <ListItemButton
      sx={{
        px: 0,
        py: "10px",
        borderRadius: "8px",
        cursor: hasManyQuotes ? "pointer" : "default",
        "&:hover": { backgroundColor: "transparent" },
      }}
      aria-controls={open ? "StudentBookedQuoteMenu" : undefined}
      aria-expanded={open ? "true" : undefined}
      onClick={hasManyQuotes ? openMenu : undefined}
      id="StudentBookedQuoteMenuButton"
      aria-haspopup="listbox"
    >
      <Row gap="10px">
        <_QuoteIconsStack quotes={bookedQuotes} selectedQuote={selectedBookedQuote} />
        <ListItemText
          sx={{
            my: 0,
            color: "text.primary",
            flexGrow: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          secondaryTypographyProps={{
            sx: { fontWeight: "normal", fontSize: 12, color: "text.secondary" },
          }}
          primary={
            <Row sx={{ alignItems: "center", gap: "4px" }}>
              <Typography variant="body2" sx={{ fontSize: "19px", fontWeight: 600, color: "text.primary" }}>
                {selectedBookedQuote.drivingLicenseClasses.join(", ")}
              </Typography>
              {bookedQuotes.length > 1 ? (
                <ExpandMoreIcon
                  sx={{
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    fontSize: 20,
                    color: "text.primary",
                  }}
                />
              ) : null}
            </Row>
          }
          secondary={secondaryText}
        />
      </Row>
    </ListItemButton>
  );
}

interface _BookedQuotesMenuProps {
  anchorEl: null | HTMLElement;
  closeMenu: () => void;
  bookedQuotes: BookedQuote[];
  selectedBookedQuote: BookedQuote;
  setSelectedBookedQuote: (quote: BookedQuote) => void;
}
function _BookedQuotesMenu({
  anchorEl,
  closeMenu,
  bookedQuotes,
  selectedBookedQuote,
  setSelectedBookedQuote,
}: _BookedQuotesMenuProps) {
  const open = !!anchorEl;

  return (
    <Menu
      id="StudentBookedQuoteMenu"
      anchorEl={anchorEl}
      open={open}
      onClose={closeMenu}
      MenuListProps={{
        "aria-labelledby": "StudentBookedQuoteMenuButton",
        role: "listbox",
      }}
      slotProps={{
        paper: {
          sx: {
            "& .MuiMenu-list": { py: 0 },
            minWidth: 380,
          },
        },
      }}
      autoFocus={false}
      disableAutoFocusItem
    >
      {bookedQuotes.map((quote) => (
        <MenuItem
          key={quote.id}
          selected={selectedBookedQuote?.id === quote.id}
          onClick={() => {
            setSelectedBookedQuote(quote);
            closeMenu();
          }}
          sx={{ py: "8px" }}
        >
          <Row gap="12px" px="4px">
            <_QuoteIcon
              variant="large"
              drivingLicenseClasses={quote.drivingLicenseClasses}
              muted={!!quote.isCancelled}
              sx={{ border: 0 }}
            />
            <ListItemText
              sx={{
                color: "text.primary",
                flexGrow: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
              primaryTypographyProps={{
                sx: { fontWeight: 600, fontSize: 14, color: "text.primary" },
              }}
              primary={quote.drivingLicenseClasses.join(", ")}
              secondary={
                <Row sx={{ alignItems: "center", gap: "6px" }}>
                  <Box
                    sx={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      backgroundColor: quote.isCancelled
                        ? autovioColors.red
                        : quote.isFinished
                          ? autovioColors.grey
                          : autovioColors.green,
                    }}
                  />
                  {quote.cancelledAt ? (
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "11px", fontWeight: "normal", color: "text.secondary" }}
                    >
                      {`Abgebrochen am ${quote.cancelledAt.toFormat("dd.MM.yyyy")}`}
                    </Typography>
                  ) : (
                    quote.createdAt && (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "11px", fontWeight: "normal", color: "text.secondary" }}
                      >
                        {`Gestartet am ${quote.createdAt.toFormat("dd.MM.yyyy")}`}
                      </Typography>
                    )
                  )}
                </Row>
              }
            />
          </Row>
        </MenuItem>
      ))}
    </Menu>
  );
}

function _QuoteIconsStack({ quotes, selectedQuote }: { quotes: BookedQuote[]; selectedQuote?: BookedQuote }) {
  const otherBookedQuotes = quotes.filter((quote) => quote.bundleName !== selectedQuote?.bundleName);
  return (
    <Row sx={{ position: "relative", height: "40px", width: `${42 + (quotes.length - 1) * 8}px` }}>
      {[...otherBookedQuotes, selectedQuote].map((quote, i) => (
        <_QuoteIcon
          variant="large"
          key={quote?.id}
          drivingLicenseClasses={quote?.id === selectedQuote?.id ? quote?.drivingLicenseClasses : undefined}
          muted={!!quote?.isCancelled}
          sx={{ position: "absolute", left: `${i * 8}px`, top: 0 }}
        />
      ))}
    </Row>
  );
}

interface QuoteIconProps {
  variant?: "small" | "large";
  drivingLicenseClasses?: DrivingLicenseClass[];
  sx?: SxProps;
  muted?: boolean;
}
function _QuoteIcon({ variant = "small", drivingLicenseClasses, sx = {}, muted }: QuoteIconProps) {
  const iconSize = variant === "small" ? 32 : 40;
  const vehicleIconSize = iconSize * 0.6;
  const firstDrivingLicenseClass = drivingLicenseClasses?.[0];
  const iconColor = muted ? "text.secondary" : "text.primary";
  return (
    <Box
      sx={{
        width: `${iconSize}px`,
        height: `${iconSize}px`,
        borderRadius: "50%",
        backgroundColor: "action.hover",
        border: "2px solid",
        borderColor: muted ? "divider" : "background.paper",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "content-box",
        ...sx,
      }}
    >
      {firstDrivingLicenseClass ? (
        <Column alignItems="center">
          {isForMotorcycle(firstDrivingLicenseClass) && (
            <MotorcycleIcon sx={{ fontSize: vehicleIconSize, color: iconColor }} />
          )}
          {isForCarOnly(firstDrivingLicenseClass) && <CarIcon sx={{ fontSize: vehicleIconSize, color: iconColor }} />}
          {isForCarWithTrailer(firstDrivingLicenseClass) && (
            <TrailerIcon sx={{ fontSize: vehicleIconSize, color: iconColor }} />
          )}
          {isForTruck(firstDrivingLicenseClass) && <TruckIcon sx={{ fontSize: vehicleIconSize, color: iconColor }} />}
          {isForBus(firstDrivingLicenseClass) && <BusIcon sx={{ fontSize: vehicleIconSize, color: iconColor }} />}
          {isForTractor(firstDrivingLicenseClass) && (
            <TractorIcon sx={{ fontSize: vehicleIconSize, color: iconColor }} />
          )}
          {drivingLicenseClasses.length > 1 && (
            <Typography fontSize="8px" fontWeight={600} lineHeight={1.88} color={iconColor} mt="-2px">
              +{drivingLicenseClasses.length - 1}
            </Typography>
          )}
        </Column>
      ) : null}
    </Box>
  );
}

function _ManageBookedQuotes({ student, bookedQuotes }: { student: Student; bookedQuotes: BookedQuote[] }) {
  const { dialogProps: bookedQuotesDialogProps, openDialog: openBookedQuotesDialog } = useDialog("BookedQuotesDialog");
  const { dialogProps: addBundleDialogProps, openDialog: openAddBundleDialog } = useDialog("AddBundleDialog");
  const setBookedQuoteShownInDialog = useSetRecoilState(bookedQuoteShownInBookedQuoteDialogState);
  const autoOpenBookedQuoteDialogRef = useRef(true);

  useEffect(() => {
    if (!bookedQuotes || !autoOpenBookedQuoteDialogRef.current) {
      return;
    }
    autoOpenBookedQuoteDialogRef.current = false;
    const openDialog = new URL(location.href).searchParams.get("openDialog");
    if (openDialog && openDialog.startsWith("BookedQuoteDialog!")) {
      const bookedQuoteId = openDialog.substring("BookedQuoteDialog!".length);
      const bookedQuote = bookedQuotes.find((it) => it.id === bookedQuoteId);
      if (bookedQuote) {
        setBookedQuoteShownInDialog(bookedQuote);
      }
    }
  }, [bookedQuotes]);

  if (bookedQuotes.length === 0) return null;

  return (
    <Box sx={{ py: "10px" }}>
      {grants.has("BookedQuotes:list") && bookedQuotes && (
        <IconButton onClick={openBookedQuotesDialog} sx={{ p: 0 }}>
          <SettingsOutlinedIcon fontSize="small" sx={{ color: "text.primary" }} />
        </IconButton>
      )}
      <BookedQuotesDialog
        {...bookedQuotesDialogProps}
        student={student}
        addBundleButton={
          <Button startIcon={<AddIcon />} variant="outlined" onClick={openAddBundleDialog}>
            Paket hinzufügen
          </Button>
        }
      />
      <BookedQuoteDialog />
      <AddBundleDialog {...addBundleDialogProps} student={student} />
    </Box>
  );
}
